import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { downloadCsvExport } from '@/utils/download-csv';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertTriangle, ExternalLink, Filter, X, Download } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { DateRangePicker, Range } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';

interface BlogInfo {
    blog_id: number;
    title: string;
    slug: string;
    author: string;
    category_name: string;
}

interface MisinformationReport {
    id: number;
    blog_id: number;
    slate_id: number;
    name: string;
    email: string;
    phone: string;
    message: string;
    date_created: string;
    blog?: BlogInfo;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const BlogMisinformationAdmin: React.FC = () => {
    const [reports, setReports] = useState<MisinformationReport[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });

    // Date range filter state
    const [dateRange, setDateRange] = useState<Range[] | null>(null);
    const [dateRangeDraft, setDateRangeDraft] = useState<Range[]>([
        { startDate: new Date(), endDate: new Date(), key: 'selection' },
    ]);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Modal state
    const [selectedReport, setSelectedReport] = useState<MisinformationReport | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch reports
    const fetchReports = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const dateParams = dateRange
                ? `&date_from=${moment(dateRange[0].startDate).format('YYYY-MM-DD')}&date_to=${moment(dateRange[0].endDate).format('YYYY-MM-DD')}`
                : '';
            const response = await apiRequest(`/api/blog-misinformation?page=${page}&limit=${pagination.itemsPerPage}&search=${encodeURIComponent(searchTerm)}${dateParams}`, {
                method: 'GET',
            });

            if (response.success && response.data) {
                // response.data contains: { success, totalReports, totalPages, currentPage, data: [...] }
                const apiData = response.data;
                setReports(apiData.data || []);
                setPagination({
                    currentPage: apiData.currentPage || page,
                    totalPages: apiData.totalPages || 1,
                    totalItems: apiData.totalReports || 0,
                    itemsPerPage: pagination.itemsPerPage,
                });
            } else {
                showToast('Failed to fetch misinformation reports', 'error');
            }
        } catch (error) {
            console.error('Error fetching misinformation reports:', error);
            showToast('Failed to fetch misinformation reports', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load reports on mount and when search or date filter changes
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchReports(1);
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, dateRange]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchReports(newPage);
        }
    };

    // Handle clearing the date range filter
    const handleClearDateRange = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDateRange(null);
    };

    // Handle opening the date range picker (seed draft from currently applied range)
    const handleToggleDateRangePicker = () => {
        if (!showDateRangePicker) {
            setDateRangeDraft(dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
        }
        setShowDateRangePicker(!showDateRangePicker);
    };

    // Handle applying the draft date range as the active filter
    const handleApplyDateRange = () => {
        setDateRange(dateRangeDraft);
        setShowDateRangePicker(false);
    };

    // Handle cancelling the in-progress date selection
    const handleCancelDateRange = () => {
        setShowDateRangePicker(false);
    };

    // Handle exporting the currently filtered reports as CSV
    const handleExport = async () => {
        try {
            setIsExporting(true);
            await downloadCsvExport('/api/blog-misinformation/export', {
                search: searchTerm,
                date_from: dateRange ? moment(dateRange[0].startDate).format('YYYY-MM-DD') : null,
                date_to: dateRange ? moment(dateRange[0].endDate).format('YYYY-MM-DD') : null,
            });
        } catch (error) {
            console.error('Error exporting reports:', error);
            showToast('Failed to export reports', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    // Handle report click
    const handleReportClick = (report: MisinformationReport) => {
        setSelectedReport(report);
        setIsModalOpen(true);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedReport(null);
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM');
        return `${dateStr}, ${timeStr}`;
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge - 25% */}
                    <div className="flex items-center gap-3" style={{ flex: '0 0 25%' }}>
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-orange-500 rounded-lg text-white">
                                <AlertTriangle className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Blog Misinformation</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-500 text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">User-reported factual errors in blogs</p>
                        </div>
                    </div>

                    {/* Right Side - Search - 75% */}
                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 75%' }}>
                        {/* Search */}
                        <div className="relative" style={{ width: '300px' }}>
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by blog title, reporter name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Reported On Date Filter */}
                        <div className="relative">
                            <button
                                className="border border-gray-300 rounded-md px-3 py-2 inline-flex items-center gap-2 hover:bg-gray-50"
                                onClick={handleToggleDateRangePicker}
                            >
                                {dateRange ? (
                                    <>
                                        {moment(dateRange[0].startDate).format('MMM D')} - {moment(dateRange[0].endDate).format('MMM D')}
                                        <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" onClick={handleClearDateRange} />
                                    </>
                                ) : (
                                    <>
                                        Reported On
                                        <Filter className="h-3.5 w-3.5" />
                                    </>
                                )}
                            </button>

                            {showDateRangePicker && (
                                <div className="absolute right-0 mt-2 z-[9999] shadow-lg rounded-md bg-white border border-gray-200">
                                    <DateRangePicker
                                        ranges={dateRangeDraft}
                                        onChange={(item) => setDateRangeDraft([item.selection as Range])}
                                        months={1}
                                        direction="horizontal"
                                        editableDateInputs
                                    />
                                    <div className="flex justify-end gap-2 px-3 py-2 border-t border-gray-200">
                                        <Button variant="outline" size="sm" onClick={handleCancelDateRange}>
                                            Cancel
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={handleApplyDateRange}>
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Export */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                            leftIcon={<Download size={16} />}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    ID
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Blog Title
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Reporter Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Phone
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Message
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Reported On
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        Loading reports...
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        No misinformation reports found
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report, index) => (
                                    <tr
                                        key={report.id}
                                        className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => handleReportClick(report)}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {report.id}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-800 font-medium max-w-xs truncate" title={report.blog?.title}>
                                            {report.blog?.title || `Blog #${report.blog_id}`}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {report.name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {report.phone || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={report.message}>
                                            {report.message ? (report.message.length > 50 ? `${report.message.substring(0, 50)}...` : report.message) : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                            {formatDate(report.date_created)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    {pagination.totalItems > 0 ? (
                        <>
                            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                            {pagination.totalItems} results
                        </>
                    ) : (
                        'No results'
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Report Detail Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Misinformation Report"
                subtitle={selectedReport ? `Report #${selectedReport.id}` : ''}
                icon={<div className="text-orange-500"><AlertTriangle size={24} /></div>}
                width="600px"
            >
                {selectedReport && (
                    <>
                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Blog Info */}
                            <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                                <h3 className="font-semibold text-gray-900">
                                    {selectedReport.blog?.title || `Blog #${selectedReport.blog_id}`}
                                </h3>
                                <div className="space-y-1 text-sm text-gray-600">
                                    {selectedReport.blog ? (
                                        <>
                                            <div>Author: {selectedReport.blog.author || '-'}</div>
                                            <div>Category: {selectedReport.blog.category_name || '-'}</div>
                                        </>
                                    ) : (
                                        <div className="text-gray-500">Blog ID: {selectedReport.blog_id}</div>
                                    )}
                                    {selectedReport.blog?.slug && (
                                        <a
                                            href={`https://yellowslate.com/blog/${selectedReport.blog.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                        >
                                            View Blog <ExternalLink size={14} />
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Reporter Info */}
                            <div className="border-t pt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Reporter Information</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Name</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedReport.name || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Phone</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedReport.phone || '-'}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs text-gray-500 uppercase">Email</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedReport.email || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Report Details */}
                            <div className="border-t pt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Report Details</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Reported On</label>
                                        <p className="text-sm font-medium text-gray-900">{formatDate(selectedReport.date_created)}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Message</label>
                                        <div className="mt-1 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedReport.message || 'No message provided'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Fixed Footer */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="outline"
                                    onClick={handleCloseModal}
                                    className="border-0"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>
        </div>
    );
};

export default BlogMisinformationAdmin;
